import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Search, Boxes, AlertTriangle, Upload, X, FileSpreadsheet } from "lucide-react";
import { StockTypeTabs, FormModal, Field, SectionTitle } from "@/pages/InwardStock";

export default function StockInHand() {
  const [stockType, setStockType] = useState("ready");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showUpload, setShowUpload] = useState(false);

  const load = () => api.get(`/stock/in-hand?stock_type=${stockType}`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stockType]);

  const filtered = rows.filter((r) => {
    const matches = !q ||
      r.batch_number.toLowerCase().includes(q.toLowerCase()) ||
      (r.brand || "").toLowerCase().includes(q.toLowerCase()) ||
      (r.model || "").toLowerCase().includes(q.toLowerCase());
    const passFilter =
      filter === "all" ||
      (filter === "low" && r.low_stock) ||
      (filter === "empty" && r.in_hand === 0) ||
      (filter === "active" && r.in_hand > 0);
    return matches && passFilter;
  });

  const totals = filtered.reduce((acc, r) => ({
    in_hand: acc.in_hand + r.in_hand,
    total_in: acc.total_in + r.total_in,
    total_out: acc.total_out + r.total_out,
    value: acc.value + (r.in_hand * r.unit_cost),
  }), { in_hand: 0, total_in: 0, total_out: 0, value: 0 });

  const label = stockType === "raw" ? "Raw Material" : "Ready Stock";

  return (
    <div className="p-8 space-y-6" data-testid="stock-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Inventory · {label}</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Stock In Hand</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">By batch · Real-time reconciled</div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowUpload(true)} className="btn-ghost flex items-center gap-2" data-testid="upload-stock-btn">
            <Upload className="w-4 h-4" /> Upload Stock
          </button>
          <a href={`${API}/export/stock-in-hand?stock_type=${stockType}`} className="btn-ghost flex items-center gap-2" data-testid="export-stock-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      <StockTypeTabs value={stockType} onChange={setStockType} testidPrefix="stock" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="kpi-card"><div className="overline">Batches</div><div className="font-display text-3xl mt-2 text-zinc-900">{filtered.length}</div></div>
        <div className="kpi-card"><div className="overline">Units In Hand</div><div className="font-display text-3xl mt-2 text-[#16A34A]">{totals.in_hand}</div></div>
        <div className="kpi-card"><div className="overline">Total Inward</div><div className="font-display text-3xl mt-2 text-zinc-900">{totals.total_in}</div></div>
        <div className="kpi-card"><div className="overline">Estimated Value</div><div className="font-display text-3xl mt-2 text-zinc-900">₹{totals.value.toLocaleString()}</div></div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="stock-search"
            placeholder="Search by batch, brand, model…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="input-field w-full pl-10"
          />
        </div>
        {[
          { k: "all", label: "All" },
          { k: "active", label: "Active" },
          { k: "low", label: "Low Stock" },
          { k: "empty", label: "Depleted" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k}`}
            className={`px-4 py-2 border text-xs uppercase font-bold tracking-widest transition-colors ${
              filter === f.k ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#16A34A] hover:text-[#16A34A]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="stock-table">
          <thead>
            <tr>
              <th>Batch #</th>
              <th>Brand / Model</th>
              <th>Type</th>
              <th>Spec</th>
              <th>Source</th>
              <th>Date</th>
              <th className="text-right">In</th>
              <th className="text-right">Out</th>
              <th className="text-right">In Hand</th>
              <th className="text-right">Unit ₹</th>
              <th className="text-right">Stock Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="12" className="text-center py-12 text-zinc-500 font-sans">
                <Boxes className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No batches match the filter.
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.batch_number} data-testid={`stock-row-${r.batch_number}`}>
                <td className="text-[#16A34A] font-semibold">{r.batch_number}</td>
                <td className="font-sans">{r.brand} <span className="text-zinc-400">/</span> {r.model}</td>
                <td className="font-sans">{r.battery_type}</td>
                <td>{r.voltage}V · {r.capacity_ah}Ah</td>
                <td className="font-sans">{r.vendor_name}</td>
                <td>{r.purchase_date}</td>
                <td className="text-right">{r.total_in}</td>
                <td className="text-right">{r.total_out}</td>
                <td className={`text-right font-bold ${r.in_hand === 0 ? "text-[#DC2626]" : r.low_stock ? "text-[#D97706]" : "text-zinc-900"}`}>{r.in_hand}</td>
                <td className="text-right">{r.unit_cost.toLocaleString()}</td>
                <td className="text-right">₹{(r.in_hand * r.unit_cost).toLocaleString()}</td>
                <td>
                  {r.in_hand === 0 ? (
                    <span className="badge-danger">Depleted</span>
                  ) : r.low_stock ? (
                    <span className="badge-warn"><AlertTriangle className="w-3 h-3 inline mr-1" />Low</span>
                  ) : (
                    <span className="badge-ok">Healthy</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <UploadStockModal
          stockType={stockType}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); load(); }}
        />
      )}
    </div>
  );
}

function UploadStockModal({ stockType, onClose, onDone }) {
  const [tab, setTab] = useState("file"); // "file" | "manual"
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState({
    batch_number: "",
    brand: "",
    model: "",
    battery_type: "",
    voltage: "",
    capacity_ah: "",
    quantity: 1,
    unit_cost: 0,
    as_of_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("stock_type", stockType);
      const r = await api.post("/opening-stock/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/opening-stock", {
        ...manual,
        stock_type: stockType,
        quantity: parseInt(manual.quantity),
        unit_cost: parseFloat(manual.unit_cost || 0),
      });
      toast.success("Opening stock added");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const label = stockType === "raw" ? "Raw Material" : "Ready Stock";

  const downloadTemplate = () => {
    const csv = "batch_number,brand,model,battery_type,voltage,capacity_ah,quantity,unit_cost,location,as_of_date,notes\nBAT-2026-001,Exide,FML150,Li-ion,12,150,10,4500,WH-01,2026-02-01,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "stock_upload_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <FormModal onClose={onClose}>
      <div className="space-y-5" data-testid="upload-modal">
        <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
          <div>
            <h2 className="font-display text-2xl uppercase text-zinc-900">Upload · {label}</h2>
            <div className="text-xs text-zinc-500 mt-1 font-sans">
              Add opening stock directly to <b>{label}</b>. Creates Stock in Hand entries (no Inward record).
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 border-b border-zinc-200">
          {[{ k: "file", label: "CSV / Excel Upload" }, { k: "manual", label: "Single Entry" }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`upload-tab-${t.k}`}
              className={`px-4 py-2 -mb-px text-xs uppercase font-bold tracking-widest border-b-2 transition-colors ${
                tab === t.k ? "border-[#16A34A] text-[#16A34A]" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "file" && (
          <div className="space-y-4">
            <div className="border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center rounded">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-[#16A34A]" />
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-sm"
                data-testid="upload-file-input"
              />
              <div className="text-xs text-zinc-500 mt-3 font-sans">
                Accepts <b>.csv, .xlsx, .xls</b>. Columns:{" "}
                <span className="font-mono">batch_number, brand, model, battery_type, voltage, capacity_ah, quantity, unit_cost, location, as_of_date, notes</span>
              </div>
              <button onClick={downloadTemplate} className="mt-3 text-xs text-[#16A34A] hover:underline" data-testid="download-template-btn">
                Download CSV Template
              </button>
            </div>

            {result && (
              <div className="border border-zinc-200 bg-white p-4 rounded text-sm space-y-2" data-testid="upload-result">
                <div><b>Total rows:</b> {result.total_rows} · <b>Imported:</b> <span className="text-[#16A34A] font-bold">{result.created}</span></div>
                {result.errors?.length > 0 && (
                  <div>
                    <b className="text-[#DC2626]">{result.errors.length} errors:</b>
                    <ul className="mt-1 text-xs font-mono list-disc pl-5">
                      {result.errors.slice(0, 20).map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={onClose} className="btn-ghost">Close</button>
              <button type="button" onClick={upload} disabled={!file || uploading} className="btn-primary" data-testid="submit-upload">
                {uploading ? "Uploading…" : "Upload File"}
              </button>
              {result && result.created > 0 && (
                <button type="button" onClick={onDone} className="btn-primary" data-testid="upload-done">Done</button>
              )}
            </div>
          </div>
        )}

        {tab === "manual" && (
          <form onSubmit={submitManual} className="space-y-4" data-testid="manual-form">
            <SectionTitle>Opening Stock Entry</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Batch Number*"><input required value={manual.batch_number} onChange={(e) => setManual({ ...manual, batch_number: e.target.value.toUpperCase() })} className="input-field font-mono" data-testid="man-batch" /></Field>
              <Field label="Brand"><input value={manual.brand} onChange={(e) => setManual({ ...manual, brand: e.target.value })} className="input-field" data-testid="man-brand" /></Field>
              <Field label="Model"><input value={manual.model} onChange={(e) => setManual({ ...manual, model: e.target.value })} className="input-field" data-testid="man-model" /></Field>
              <Field label="Battery Type"><input value={manual.battery_type} onChange={(e) => setManual({ ...manual, battery_type: e.target.value })} className="input-field" data-testid="man-type" /></Field>
              <Field label="Voltage"><input value={manual.voltage} onChange={(e) => setManual({ ...manual, voltage: e.target.value })} className="input-field font-mono" data-testid="man-voltage" /></Field>
              <Field label="Capacity (Ah)"><input value={manual.capacity_ah} onChange={(e) => setManual({ ...manual, capacity_ah: e.target.value })} className="input-field font-mono" data-testid="man-cap" /></Field>
              <Field label="Quantity*"><input required type="number" min="1" value={manual.quantity} onChange={(e) => setManual({ ...manual, quantity: e.target.value })} className="input-field font-mono" data-testid="man-qty" /></Field>
              <Field label="Unit Cost (₹)"><input type="number" step="0.01" value={manual.unit_cost} onChange={(e) => setManual({ ...manual, unit_cost: e.target.value })} className="input-field font-mono" data-testid="man-cost" /></Field>
              <Field label="As Of Date"><input type="date" value={manual.as_of_date} onChange={(e) => setManual({ ...manual, as_of_date: e.target.value })} className="input-field" data-testid="man-date" /></Field>
              <Field label="Notes" full><textarea value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })} className="input-field h-16 resize-none" data-testid="man-notes" /></Field>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-manual">Add Entry</button>
            </div>
          </form>
        )}
      </div>
    </FormModal>
  );
}
